import { create, v, w } from '@dojo/framework/core/vdom';
import { focus } from '@dojo/framework/core/middleware/focus';
import { theme } from '@dojo/framework/core/middleware/theme';
import { Keys } from '@holmescorp/common';
import { lccstate, keymap, pathmap, quizmap, createNlsMiddleware } from 'lcc-state';
import { ResourceParser } from '@holmescorp/resource-parser';
import { TopicTitle } from './topic-title';
import { TopicsetShortdesc } from './topicset-shortdesc';
import { QuizButton } from '../support/quiz-button';
import { FlashCardsButton } from '../support/flashcards-button';
import { CaseStudyButton } from '../support/casestudy-button';
import { DiscussionButton } from '../support/discussion-button';
import css from '../css/topicaccordion.m.css';
const nls = createNlsMiddleware('ditabook');
const resources = createNlsMiddleware('resources');
const settings = createNlsMiddleware('settings');
const factory = create({ theme, nls, focus, resources, settings, lccstate }).properties();
export const TopicsetBody = factory(({ properties, middleware: { theme, nls, focus, resources, settings, lccstate } }) => {
    const { kids, key, isOpen } = properties();
    const themeCss = theme.classes(css);
    const onlytg = kids.filter((val) => !('shortdesc' in val));
    const { quiz, flashcards, discussion, casestudies } = quizmap.get(key) || {};
    const hasQuiz = Boolean(quiz) || Boolean(flashcards);
    const { messages: { quiz: quizText = 'Quiz', flashcards: flashcardText = 'Flashcards', discussions: discussionsText = 'Discussions' } } = nls();
    const { messages: resourceMessages } = resources();
    const hasResources = key in resourceMessages;
    const caseStudies = casestudies && casestudies.length
        ? casestudies.map((casestudyid) => {
            return v('div', { role: 'listitem' }, [
                w(CaseStudyButton, {
                    topicid: key,
                    quizid: casestudyid
                })
            ]);
        })
        : [];
    const quizBlock = hasQuiz
        ? [
            quiz
                ? v('div', { role: 'listitem' }, [w(QuizButton, { topicid: key, text: quizText })])
                : null,
            flashcards
                ? v('div', { role: 'listitem' }, [
                    w(FlashCardsButton, { topicid: key, text: flashcardText })
                ])
                : null,
            discussion
                ? v('div', { role: 'listitem' }, [
                    w(DiscussionButton, { topicid: key, text: discussionsText })
                ])
                : null,
            ...caseStudies
        ]
        : [];
    const resourcesBlock = hasResources
        ? [w(ResourceParser, { messages: resourceMessages, topicid: `${key}` })]
        : [];
    const { messages: settingsMessages } = settings();
    let allowed_topics = [];
    const dat = settingsMessages.demo_allowed_topics;
    if (dat) {
        try {
            allowed_topics = JSON.parse(dat);
        }
        catch (e) {
            console.warn(`json parse error ${e}`, dat);
        }
    }
    const maybeFocus = (key) => {
        const tracking = lccstate.get(lccstate.path('tracking'));
        if (tracking && key === tracking.topicgroup) {
            focus.focus();
        }
    };
    return v('div', { key: `${key}-topicset`, classes: [themeCss.accordionBody] }, [
        v('div', { key: `${key}-topicgroup-wrapper`, classes: [themeCss.itemWrapper] }, [
            isOpen ? w(TopicsetShortdesc, { key, kids, isOpen }) : null,
            ...onlytg.map((val) => {
                const { attributes = {} } = val['topicgroup'];
                const { title, key, readtime } = attributes;
                return v('div', {
                    key: `${key}-topicgroup`,
                    xqa: `topicgroup ${key}`,
                    classes: [themeCss.item],
                    role: 'button',
                    tabIndex: isOpen ? 0 : -1,
                    focus: () => focus.shouldFocus(),
                    onFocus: maybeFocus(key),
                    onclick: (event) => {
                        event.stopPropagation();
                        if (settingsMessages.is_demo === 'true' && allowed_topics.indexOf(key) === -1) {
                            return;
                        }
                        const { path } = keymap.get(key);
                        const { view } = pathmap.get(path);
                        view();
                    },
                    onkeydown: (event) => {
                        const { path } = keymap.get(key);
                        const { view } = pathmap.get(path);
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                if (settingsMessages.is_demo === 'true' && allowed_topics.indexOf(key) === -1) {
                                    return;
                                }
                                view();
                        }
                    }
                }, [v('div', {}, []), w(TopicTitle, { title, key, useTitle: true, readtime })]);
            }),
            v('div', {
                key: `${key}-action-block`,
                xqa: `${key} actionblock`,
                role: 'list',
                classes: [themeCss.actionBlock]
            }, hasResources ? [...quizBlock.flat(), ...resourcesBlock] : [...quizBlock.flat()])
        ])
    ]);
});
